import { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { ChevronLeft, ChevronRight, Plus, Trash2 } from "lucide-react"
import { Button } from "../../components/ui/Button"
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/Card"
import { Input } from "../../components/ui/Input"
import { Select } from "../../components/ui/Select"
import { fr } from "../../locales/fr"
import { useAuth } from "../../context/AuthContext"
import { canManageTeamAndCourses, isPureCoach } from "../../utils/roles"
import { getCourseSessions, createCourseSession, deleteCourseSession } from "../../services/courseSessionService"
import { getCourses } from "../../services/courseService"
import { getTrainers } from "../../services/trainerService"
import SessionMonthGrid, { formatYmdFromDate } from "./SessionMonthGrid"

function startOfWeekMonday(d) {
  const date = new Date(d)
  const day = date.getDay()
  const diff = day === 0 ? -6 : 1 - day
  date.setDate(date.getDate() + diff)
  date.setHours(0, 0, 0, 0)
  return date
}

function sessionDayKey(iso) {
  return formatYmdFromDate(new Date(iso))
}

const EMPTY_WEEK_KEYS = new Set()

const emptySessionForm = () => ({
  course_id: "",
  trainer_id: "",
  sessionDate: "",
  startTime: "09:00",
  endTime: "10:00",
  room: "",
  max_participants: "",
})

export default function CourseCalendar() {
  const navigate = useNavigate()
  const { trainerId: trainerIdParam } = useParams()
  const { user } = useAuth()
  const canEdit = canManageTeamAndCourses(user)
  const coachOnly = isPureCoach(user)
  const cal = fr.calendarPage

  const [weekStart, setWeekStart] = useState(() => startOfWeekMonday(new Date()))
  const [viewMonth, setViewMonth] = useState(() => {
    const w = startOfWeekMonday(new Date())
    return { y: w.getFullYear(), m: w.getMonth() }
  })
  const [sessions, setSessions] = useState([])
  const [courses, setCourses] = useState([])
  const [trainers, setTrainers] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [sessionForm, setSessionForm] = useState(emptySessionForm)
  const [scheduleMode, setScheduleMode] = useState("single")
  const [multiDates, setMultiDates] = useState([])
  const [formViewMonth, setFormViewMonth] = useState(() => ({
    y: new Date().getFullYear(),
    m: new Date().getMonth(),
  }))

  const { from, to, dayLabels } = useMemo(() => {
    const fromD = new Date(weekStart)
    const toD = new Date(weekStart)
    toD.setDate(toD.getDate() + 6)
    const labels = []
    for (let i = 0; i < 7; i++) {
      const x = new Date(weekStart)
      x.setDate(x.getDate() + i)
      labels.push({
        key: formatYmdFromDate(x),
        label: x.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" }),
      })
    }
    return { from: formatYmdFromDate(fromD), to: formatYmdFromDate(toD), dayLabels: labels }
  }, [weekStart])

  const weekDayKeys = useMemo(() => new Set(dayLabels.map((d) => d.key)), [dayLabels])

  const selectedTrainerId = trainerIdParam ? String(parseInt(trainerIdParam, 10)) : ""

  useEffect(() => {
    setViewMonth({ y: weekStart.getFullYear(), m: weekStart.getMonth() })
  }, [weekStart])

  useEffect(() => {
    let cancelled = false
    const loadMeta = async () => {
      try {
        const [c, t] = await Promise.all([getCourses(), getTrainers()])
        if (!cancelled) {
          setCourses(Array.isArray(c) ? c : [])
          setTrainers(Array.isArray(t) ? t : [])
        }
      } catch (e) {
        console.error(e)
      }
    }
    loadMeta()
    return () => {
      cancelled = true
    }
  }, [])

  const reloadSessions = useCallback(
    async (fromStr, toStr) => {
      setLoading(true)
      try {
        const data = await getCourseSessions({
          from: fromStr,
          to: toStr,
          ...(selectedTrainerId ? { trainer_id: selectedTrainerId } : {}),
        })
        setSessions(Array.isArray(data) ? data : [])
      } catch (e) {
        console.error(e)
        setSessions([])
      } finally {
        setLoading(false)
      }
    },
    [selectedTrainerId]
  )

  useEffect(() => {
    reloadSessions(from, to)
  }, [from, to, reloadSessions])

  const byDay = useMemo(() => {
    const map = {}
    dayLabels.forEach((d) => {
      map[d.key] = []
    })
    sessions.forEach((s) => {
      const k = sessionDayKey(s.startsAt)
      if (!map[k]) map[k] = []
      map[k].push(s)
    })
    Object.keys(map).forEach((k) => {
      map[k].sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt))
    })
    return map
  }, [sessions, dayLabels])

  const onTrainerSelect = (e) => {
    const v = e.target.value
    if (!v) navigate("/calendar")
    else navigate(`/calendar/coach/${v}`)
  }

  const openNewSessionForDay = useCallback(
    (ymd) => {
      setScheduleMode("single")
      setMultiDates([])
      setSessionForm({
        course_id: courses[0]?.id ? String(courses[0].id) : "",
        trainer_id: selectedTrainerId || (trainers[0]?.id ? String(trainers[0].id) : ""),
        sessionDate: ymd,
        startTime: "09:00",
        endTime: "10:00",
        room: "",
        max_participants: "",
      })
      setShowForm(true)
    },
    [courses, trainers, selectedTrainerId]
  )

  const toggleMultiDay = useCallback((d) => {
    const ymd = formatYmdFromDate(d)
    setMultiDates((prev) => {
      if (prev.includes(ymd)) return prev.filter((x) => x !== ymd)
      return [...prev, ymd].sort()
    })
  }, [])

  const shiftFormMonth = (delta) => {
    setFormViewMonth((vm) => {
      let m = vm.m + delta
      let y = vm.y
      if (m > 11) {
        m = 0
        y++
      }
      if (m < 0) {
        m = 11
        y--
      }
      return { y, m }
    })
  }

  const handlePickDayFromGrid = useCallback(
    (d) => {
      setWeekStart(startOfWeekMonday(d))
      openNewSessionForDay(formatYmdFromDate(d))
    },
    [openNewSessionForDay]
  )

  const shiftMonth = (delta) => {
    setViewMonth((vm) => {
      let m = vm.m + delta
      let y = vm.y
      if (m > 11) {
        m = 0
        y++
      }
      if (m < 0) {
        m = 11
        y--
      }
      return { y, m }
    })
  }

  const buildPayloadFor = (ymd) => {
    const [y, mo, da] = ymd.split("-").map((x) => parseInt(x, 10))
    const [sh, sm] = sessionForm.startTime.split(":").map((x) => parseInt(x, 10))
    const [eh, em] = sessionForm.endTime.split(":").map((x) => parseInt(x, 10))
    const starts = new Date(y, mo - 1, da, sh, sm, 0, 0)
    const ends = new Date(y, mo - 1, da, eh, em, 0, 0)
    return {
      starts,
      ends,
      payload: {
        course_id: parseInt(sessionForm.course_id, 10),
        trainer_id: parseInt(sessionForm.trainer_id, 10),
        starts_at: starts.toISOString(),
        ends_at: ends.toISOString(),
        room: sessionForm.room.trim() || null,
        max_participants: sessionForm.max_participants
          ? parseInt(sessionForm.max_participants, 10)
          : null,
      },
    }
  }

  const submitSession = async (e) => {
    e.preventDefault()
    const dates =
      scheduleMode === "multi" ? [...multiDates].sort() : sessionForm.sessionDate ? [sessionForm.sessionDate] : []

    if (dates.length === 0) {
      alert(scheduleMode === "multi" ? cal.multiDaysRequired : cal.sessionDateLabel)
      return
    }

    const first = buildPayloadFor(dates[0])
    if (first.ends <= first.starts) {
      alert(cal.endAfterStart)
      return
    }

    setSaving(true)
    let ok = 0
    const errors = []
    try {
      for (const ymd of dates) {
        const { starts, ends, payload } = buildPayloadFor(ymd)
        if (ends <= starts) {
          errors.push(ymd)
          continue
        }
        try {
          await createCourseSession(payload)
          ok++
        } catch (err) {
          console.error(err)
          errors.push(ymd)
        }
      }

      if (ok === 0) {
        alert(cal.bulkPartialError.replace("{{n}}", String(errors.length)))
        setSaving(false)
        return
      }
      if (errors.length) {
        alert(cal.bulkPartialError.replace("{{n}}", String(errors.length)))
      } else if (dates.length > 1) {
        alert(cal.bulkCreated.replace("{{n}}", String(ok)))
      }

      const anchor = dates[0]
      const mon = startOfWeekMonday(new Date(anchor + "T12:00:00"))
      const end = new Date(mon)
      end.setDate(end.getDate() + 6)
      setWeekStart(mon)
      setShowForm(false)
      setSessionForm(emptySessionForm())
      setScheduleMode("single")
      setMultiDates([])
      await reloadSessions(formatYmdFromDate(mon), formatYmdFromDate(end))
    } catch (err) {
      console.error(err)
      alert(err.response?.data?.error || "Erreur")
    }
    setSaving(false)
  }

  const removeSession = async (id) => {
    if (!window.confirm(cal.confirmDeleteSession)) return
    try {
      await deleteCourseSession(id)
      await reloadSessions(from, to)
    } catch (err) {
      console.error(err)
      alert(err.response?.data?.error || "Erreur")
    }
  }

  const openNewSession = () => {
    openNewSessionForDay(formatYmdFromDate(new Date()))
  }

  const shiftWeek = (delta) => {
    setWeekStart((prev) => {
      const n = new Date(prev)
      n.setDate(n.getDate() + delta * 7)
      return n
    })
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{cal.title}</h1>
          <p className="text-muted-foreground">
            {selectedTrainerId
              ? `${cal.coachView} · ${trainers.find((t) => String(t.id) === selectedTrainerId)?.name || ""}`
              : cal.subtitle}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {trainerIdParam && (
            <Button type="button" variant="outline" onClick={() => navigate("/calendar")}>
              {cal.allCoaches}
            </Button>
          )}
          {!coachOnly && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">{cal.filterCoach}</span>
              <Select
                value={selectedTrainerId}
                onChange={onTrainerSelect}
                className="min-w-[200px]"
                disabled={coachOnly}
              >
                <option value="">{cal.allCoaches}</option>
                {trainers.map((t) => (
                  <option key={t.id} value={String(t.id)}>
                    {t.name}
                  </option>
                ))}
              </Select>
            </div>
          )}
          {canEdit && (
            <Button type="button" onClick={openNewSession}>
              <Plus className="mr-2 h-4 w-4" />
              {cal.addSession}
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-8">
        <SessionMonthGrid
          viewYear={viewMonth.y}
          viewMonth={viewMonth.m}
          onPrevMonth={() => shiftMonth(-1)}
          onNextMonth={() => shiftMonth(1)}
          onPickDay={handlePickDayFromGrid}
          weekDayKeys={weekDayKeys}
          labels={{ monthPrev: cal.monthPrev, monthNext: cal.monthNext, monthHint: cal.monthHint }}
          weekdays={cal.weekdaysShort}
        />

        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <p className="text-sm font-medium text-foreground">{cal.weekToolbar}</p>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => shiftWeek(-1)}
              aria-label={cal.weekPrev}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm font-medium tabular-nums">
              {dayLabels[0]?.label} — {dayLabels[6]?.label}
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => shiftWeek(1)}
              aria-label={cal.weekNext}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => setWeekStart(startOfWeekMonday(new Date()))}>
              {fr.common.reset}
            </Button>
          </div>
        </div>
      </div>

      {canEdit && showForm && (
        <Card>
          <CardHeader>
            <CardTitle>{cal.addSession}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={submitSession} className="grid gap-4 md:grid-cols-2">
              <div className="flex flex-wrap gap-2 md:col-span-2">
                <Button
                  type="button"
                  size="sm"
                  variant={scheduleMode === "single" ? "default" : "outline"}
                  onClick={() => {
                    setScheduleMode("single")
                    setMultiDates([])
                  }}
                >
                  {cal.scheduleModeSingle}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={scheduleMode === "multi" ? "default" : "outline"}
                  onClick={() => {
                    setScheduleMode("multi")
                    setFormViewMonth({ y: weekStart.getFullYear(), m: weekStart.getMonth() })
                  }}
                >
                  {cal.scheduleModeMulti}
                </Button>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">{cal.course}</label>
                <Select
                  value={sessionForm.course_id}
                  onChange={(e) => setSessionForm({ ...sessionForm, course_id: e.target.value })}
                  required
                >
                  <option value="">{cal.course}</option>
                  {courses.map((c) => (
                    <option key={c.id} value={String(c.id)}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">{cal.coach}</label>
                <Select
                  value={sessionForm.trainer_id}
                  onChange={(e) => setSessionForm({ ...sessionForm, trainer_id: e.target.value })}
                  required
                  disabled={coachOnly}
                >
                  <option value="">{cal.coach}</option>
                  {trainers.map((t) => (
                    <option key={t.id} value={String(t.id)}>
                      {t.name}
                    </option>
                  ))}
                </Select>
              </div>
              {scheduleMode === "single" ? (
                <div className="space-y-2">
                  <label className="text-sm font-medium">{cal.sessionDateLabel}</label>
                  <Input
                    type="date"
                    value={sessionForm.sessionDate}
                    onChange={(e) => setSessionForm({ ...sessionForm, sessionDate: e.target.value })}
                    required
                  />
                </div>
              ) : (
                <div className="space-y-3 md:col-span-2">
                  <label className="text-sm font-medium">{cal.scheduleModeMulti}</label>
                  <SessionMonthGrid
                    viewYear={formViewMonth.y}
                    viewMonth={formViewMonth.m}
                    onPrevMonth={() => shiftFormMonth(-1)}
                    onNextMonth={() => shiftFormMonth(1)}
                    onPickDay={() => {}}
                    multiSelect
                    multiSelected={multiDates}
                    onToggleMultiDay={toggleMultiDay}
                    weekDayKeys={EMPTY_WEEK_KEYS}
                    labels={{
                      monthPrev: cal.monthPrev,
                      monthNext: cal.monthNext,
                      monthHint: cal.multiMonthHint,
                    }}
                    weekdays={cal.weekdaysShort}
                  />
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                      {cal.multiDaysSelected.replace("{{count}}", String(multiDates.length))}
                    </span>
                    <Button type="button" variant="outline" size="sm" onClick={() => setMultiDates([])}>
                      {cal.clearMultiDays}
                    </Button>
                  </div>
                </div>
              )}
              <div className="space-y-2">
                <label className="text-sm font-medium">{cal.startTimeLabel}</label>
                <Input
                  type="time"
                  step={300}
                  value={sessionForm.startTime}
                  onChange={(e) => setSessionForm({ ...sessionForm, startTime: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">{cal.endTimeLabel}</label>
                <Input
                  type="time"
                  step={300}
                  value={sessionForm.endTime}
                  onChange={(e) => setSessionForm({ ...sessionForm, endTime: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">{cal.room}</label>
                <Input
                  value={sessionForm.room}
                  onChange={(e) => setSessionForm({ ...sessionForm, room: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">{cal.participants}</label>
                <Input
                  type="number"
                  min={1}
                  value={sessionForm.max_participants}
                  onChange={(e) => setSessionForm({ ...sessionForm, max_participants: e.target.value })}
                />
              </div>
              <div className="flex items-end gap-2 md:col-span-2">
                <Button
                  type="submit"
                  disabled={saving || (scheduleMode === "multi" && multiDates.length === 0)}
                >
                  {scheduleMode === "multi" && multiDates.length > 0
                    ? cal.bulkSubmitLabel.replace("{{n}}", String(multiDates.length))
                    : fr.common.save}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowForm(false)
                    setSessionForm(emptySessionForm())
                    setScheduleMode("single")
                    setMultiDates([])
                  }}
                >
                  {fr.common.cancel}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-7">
        {dayLabels.map((day) => (
          <Card key={day.key} className="flex min-h-[220px] flex-col">
            <CardHeader className="py-3">
              <CardTitle className="text-sm font-semibold capitalize">{day.label}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-1 flex-col space-y-2 pt-0" onClick={(e) => e.stopPropagation()}>
              {loading ? (
                <p className="text-xs text-muted-foreground">{fr.common.loading}</p>
              ) : (
                <>
                  {(byDay[day.key] || []).length === 0 ? (
                    <p className="text-xs text-muted-foreground">—</p>
                  ) : (
                    (byDay[day.key] || []).map((s) => (
                      <div
                        key={s.id}
                        className="rounded-md border border-border p-2 text-xs"
                        style={{
                          borderLeftWidth: 4,
                          borderLeftColor: s.course?.color || "#6366f1",
                        }}
                      >
                        <div className="font-semibold">{s.course?.name}</div>
                        <div className="text-muted-foreground">
                          {new Date(s.startsAt).toLocaleTimeString("fr-FR", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}{" "}
                          –{" "}
                          {new Date(s.endsAt).toLocaleTimeString("fr-FR", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </div>
                        <div className="text-muted-foreground">{s.trainer?.name}</div>
                        {s.room && (
                          <div className="text-muted-foreground">
                            {cal.room}: {s.room}
                          </div>
                        )}
                        {canEdit && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="mt-1 h-7 px-2 text-destructive"
                            onClick={() => removeSession(s.id)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    ))
                  )}
                  {canEdit && (
                    <div className="mt-auto border-t border-border pt-2">
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        className="w-full text-xs"
                        onClick={() => openNewSessionForDay(day.key)}
                      >
                        <Plus className="mr-1 h-3.5 w-3.5" />
                        {cal.scheduleThisDay}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {!loading && sessions.length === 0 && (
        <div className="mx-auto max-w-lg space-y-2 text-center text-sm text-muted-foreground">
          <p>{cal.emptyWeek}</p>
          <p className="leading-relaxed">{cal.emptyWeekHint}</p>
        </div>
      )}
    </div>
  )
}
